import { Guid } from "guid-typescript";
import "./tab.css";
import React, { PureComponent, ReactNode } from "react";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { CommonProps } from "../common/common-props";
import { Icon } from "../icon/icon";
import { DashboardState } from "../kuika-cl-model-runtimes";

declare let window: any;

interface TabProps {
  activeKey: string;
  defaultActiveKey: string;
  onChange?: (activeKey: string) => any;
  value?: string | number;
  selectedTabColor?: string;
  className?: string;
  children?: any;
  kuikaid?: string;
  justifiedTabTitles?: boolean;
  tabPosition?: "top" | "bottom" | "left" | "right";
  style?: React.CSSProperties;
  id?: string;
}

interface TabState {
  designTimeValue: string;
  scrollOffset: number;
  showArrows: boolean;
  showPrev: boolean;
  showNext: boolean;
}

class Tab extends PureComponent<TabProps & CommonProps, TabState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`ktab_${this.uniqueKey}`);

  private tabsRef = React.createRef<HTMLDivElement>();
  private navContainerRef = React.createRef<HTMLDivElement>();
  private navWrapRef = React.createRef<HTMLDivElement>();
  private navRef = React.createRef<HTMLDivElement>();
  private inkBarRef = React.createRef<HTMLDivElement>();

  shouldPreventOnChangeFunction: boolean = false;

  // Memoization cache for getChildren()
  private processedChildren: React.ReactElement[] = [];
  private lastChildrenProp: any = null;
  private lastChildrenStateKey: string = "";

  // Memoization cache for getClassName()
  private cachedClassName: string = "";
  private lastPropsClassName: string | undefined = undefined;

  // Cache for renderTabContent cloned elements — avoids cloneElement on unchanged tabs
  private contentCloneCache: Map<string, React.ReactElement> = new Map();
  private lastContentActiveKey: string | number | undefined = undefined;
  private lastContentParentId: string | undefined = undefined;
  private lastContentChildrenRef: React.ReactElement[] = [];
  // Cache the entire renderTabContent JSX to short-circuit React reconciliation
  private cachedTabContentJSX: ReactNode = null;

  // Cache for root class string
  private cachedRootClasses: string = "";
  private lastTabPosition: string | undefined = undefined;
  private lastRootClassName: string = "";

  private lastInkWidth = 0;
  private lastInkHeight = 0;
  private lastInkLeft = 0;
  private lastInkTop = 0;
  private lastInkTabPosition: TabProps["tabPosition"] | undefined = undefined;

  private resizeObserver: ResizeObserver | null = null;

  constructor(props: TabProps) {
    super(props);
    this.state = {
      designTimeValue: "",
      scrollOffset: 0,
      showArrows: false,
      showPrev: false,
      showNext: false
    };
  }

  initializeWindowApi = () => {
    window.kuika ??= {};
    window.kuika.tabs ??= {};
    window.kuika.tabs[this.props.kuikaid] ??= {};
    window.kuika.tabs[this.props.kuikaid].setTab = (index: number) => {
      this.handleOnChangeTab(this.props.children[index].key, true);
    };
  };

  destroyWindowApi = () => {
    delete window.kuika.tabs[this.props.kuikaid];
  };

  updateInkBar = () => {
    if (!this.tabsRef.current) return;

    const tabsElement = this.tabsRef.current;
    const activeTab = tabsElement.querySelector(".rc-tabs-tab-active") as HTMLElement;
    const inkBar = this.inkBarRef.current;

    if (activeTab && inkBar) {
      const tabPosition = this.props.tabPosition || "top";

      // Batch all reads before any writes to avoid forced relayout
      const width = activeTab.offsetWidth;
      const height = activeTab.offsetHeight;
      const left = activeTab.offsetLeft;
      const top = activeTab.offsetTop;

      // Skip writes if nothing changed — avoids layout invalidation for other tab components
      if (
        tabPosition === this.lastInkTabPosition &&
        width === this.lastInkWidth &&
        height === this.lastInkHeight &&
        left === this.lastInkLeft &&
        top === this.lastInkTop
      ) {
        return;
      }
      this.lastInkWidth = width;
      this.lastInkHeight = height;
      this.lastInkLeft = left;
      this.lastInkTop = top;
      this.lastInkTabPosition = tabPosition;

      if (tabPosition === "top" || tabPosition === "bottom") {
        inkBar.style.width = `${width}px`;
        inkBar.style.height = "2px";
        inkBar.style.transform = `translate3d(${left}px, 0px, 0px)`;
      } else {
        inkBar.style.height = `${height}px`;
        inkBar.style.width = "2px";
        inkBar.style.transform = `translate3d(0px, ${top}px, 0px)`;
      }
    }
  };

  checkOverflow = () => {
    const navEl = this.navRef.current;
    const wrapEl = this.navWrapRef.current;
    if (!navEl || !wrapEl) return;

    const tabPosition = this.props.tabPosition || "top";
    const isHorizontal = tabPosition === "top" || tabPosition === "bottom";

    const navSize = isHorizontal ? navEl.scrollWidth : navEl.scrollHeight;
    const containerSize = isHorizontal ? wrapEl.clientWidth : wrapEl.clientHeight;

    const showArrows = navSize > containerSize;
    const maxOffset = -(navSize - containerSize);
    const { scrollOffset } = this.state;

    // Reset scroll offset when tabs fit, clamp to valid range when they overflow
    const clampedOffset = showArrows ? Math.max(Math.min(scrollOffset, 0), maxOffset) : 0;

    this.setState({
      scrollOffset: clampedOffset,
      showArrows,
      showPrev: showArrows && clampedOffset < 0,
      showNext: showArrows && clampedOffset > maxOffset
    });
  };

  handleScrollPrev = () => {
    const wrapEl = this.navWrapRef.current;
    if (!wrapEl) return;

    const tabPosition = this.props.tabPosition || "top";
    const isHorizontal = tabPosition === "top" || tabPosition === "bottom";
    const containerSize = isHorizontal ? wrapEl.clientWidth : wrapEl.clientHeight;
    const step = containerSize * 0.8;

    this.setState(
      (prevState) => ({
        scrollOffset: Math.min(prevState.scrollOffset + step, 0)
      }),
      () => this.checkOverflow()
    );
  };

  handleScrollNext = () => {
    const navEl = this.navRef.current;
    const wrapEl = this.navWrapRef.current;
    if (!navEl || !wrapEl) return;

    const tabPosition = this.props.tabPosition || "top";
    const isHorizontal = tabPosition === "top" || tabPosition === "bottom";
    const navSize = isHorizontal ? navEl.scrollWidth : navEl.scrollHeight;
    const containerSize = isHorizontal ? wrapEl.clientWidth : wrapEl.clientHeight;
    const maxOffset = -(navSize - containerSize);
    const step = containerSize * 0.8;

    this.setState(
      (prevState) => ({
        scrollOffset: Math.max(prevState.scrollOffset - step, maxOffset)
      }),
      () => this.checkOverflow()
    );
  };

  scrollActiveTabIntoView = () => {
    const navEl = this.navRef.current;
    const wrapEl = this.navWrapRef.current;
    if (!navEl || !wrapEl) return;

    const activeTab = navEl.querySelector(".rc-tabs-tab-active") as HTMLElement;
    if (!activeTab) return;

    const tabPosition = this.props.tabPosition || "top";
    const isHorizontal = tabPosition === "top" || tabPosition === "bottom";

    const containerSize = isHorizontal ? wrapEl.clientWidth : wrapEl.clientHeight;
    const navSize = isHorizontal ? navEl.scrollWidth : navEl.scrollHeight;
    const tabOffset = isHorizontal ? activeTab.offsetLeft : activeTab.offsetTop;
    const tabSize = isHorizontal ? activeTab.offsetWidth : activeTab.offsetHeight;

    if (navSize <= containerSize) return;

    const { scrollOffset } = this.state;
    const maxOffset = -(navSize - containerSize);

    // Tab is to the left/above the visible area
    if (tabOffset + scrollOffset < 0) {
      this.setState({ scrollOffset: -tabOffset }, () => this.checkOverflow());
      return;
    }

    // Tab is to the right/below the visible area
    if (tabOffset + tabSize + scrollOffset > containerSize) {
      const newOffset = Math.max(-(tabOffset + tabSize - containerSize), maxOffset);
      this.setState({ scrollOffset: newOffset }, () => this.checkOverflow());
    }
  };

  componentDidMount() {
    if (window.kuika?.isDesignTime) {
      this.initializeWindowApi();
    }
    this.setDynamicStyle();

    const children = this.getChildren();
    if (children.length) {
      let activeIndex = 0;

      if (window.kuika?.dashboardState === DashboardState.design) {
        const lastActiveTab = window.kuika[`${this.props.kuikaid}_lastActiveTab`];
        if (lastActiveTab) {
          activeIndex = children.findIndex(
            (tabItem) => tabItem.key === lastActiveTab || tabItem.props?.kuikaid === lastActiveTab
          );
          if (activeIndex === -1) activeIndex = 0;
          this.setState({ designTimeValue: children[activeIndex].key });
        } else {
          activeIndex = children.findIndex(
            (tabItem) =>
              tabItem.key === this.state.designTimeValue || tabItem.props?.kuikaid === this.state.designTimeValue
          );
          if (activeIndex === -1) activeIndex = 0;
          this.setState({ designTimeValue: children[activeIndex].key });
        }
      } else if (this.props.value) {
        activeIndex = children.findIndex(
          (tabItem) => tabItem.key === this.props.value || tabItem.props?.kuikaid === this.props.value
        );
        if (activeIndex === -1) activeIndex = 0;
      }

      if (children[activeIndex] && children[activeIndex].props?.onActive) {
        children[activeIndex].props.onActive();
      }
    }

    setTimeout(() => {
      this.updateInkBar();
      this.checkOverflow();
    }, 100);

    // Observe resize for scroll arrow visibility
    if (this.navWrapRef.current && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        this.checkOverflow();
        this.updateInkBar();
      });
      this.resizeObserver.observe(this.navWrapRef.current);
    }
  }

  componentWillUnmount() {
    if (window.kuika?.isDesignTime) {
      this.destroyWindowApi();
    }
    this.style.removeStyle();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  componentDidUpdate(prevProps: Readonly<TabProps & CommonProps>, prevState: Readonly<TabState>, snapshot?: any): void {
    const { style, selectedTabColor, tabPosition, justifiedTabTitles, children } = this.props;
    const prevStyle = prevProps.style;
    const prevChildren = prevProps.children;

    const styleChanged =
      prevStyle?.color !== style?.color ||
      prevStyle?.fontSize !== style?.fontSize ||
      prevStyle?.fontFamily !== style?.fontFamily ||
      prevStyle?.fontWeight !== style?.fontWeight ||
      prevStyle?.fontStyle !== style?.fontStyle ||
      prevStyle?.lineHeight !== style?.lineHeight ||
      prevStyle?.letterSpacing !== style?.letterSpacing ||
      prevStyle?.textTransform !== style?.textTransform ||
      prevStyle?.textDecoration !== style?.textDecoration ||
      prevStyle?.textAlign !== style?.textAlign;

    if (
      styleChanged ||
      selectedTabColor !== prevProps.selectedTabColor ||
      tabPosition !== prevProps.tabPosition ||
      justifiedTabTitles !== prevProps.justifiedTabTitles ||
      children !== prevChildren
    ) {
      this.setDynamicStyle();
      setTimeout(() => {
        this.updateInkBar();
      }, 50);
    }

    // Update ink bar and scroll when active key changes
    const prevActiveKey = this.getActiveKeyFromProps(prevProps, prevState);
    const currActiveKey = this.getActiveKey();
    if (prevActiveKey !== currActiveKey) {
      setTimeout(() => {
        this.updateInkBar();
        this.scrollActiveTabIntoView();
      }, 0);
    }

    if (window.kuika?.isDesignTime) return;

    if (this.props.value !== prevProps.value) {
      if (this.shouldPreventOnChangeFunction) {
        if (this.props.onChange) this.shouldPreventOnChangeFunction = false;
      } else {
        if (this.props.onChange) this.props.onChange(`${this.props.value}`);
        this.handleTabItemOnActive(this.props.value);
      }
    }
  }

  // Helper to compute active key from arbitrary props/state (for comparison in componentDidUpdate)
  private getActiveKeyFromProps(props: TabProps & CommonProps, state: TabState): string | number | undefined {
    if (window.kuika?.dashboardState === DashboardState.design) {
      return state.designTimeValue;
    }
    let activeKey: string | number | undefined = props.value;
    if (window.kuika?.dashboardState === 1) {
      activeKey = props.activeKey;
    }
    return activeKey;
  }

  handleOnChangeTab = (activeTabKey: any, _setState?: boolean) => {
    if (window.kuika?.dashboardState === DashboardState.design) {
      this.setState({ designTimeValue: activeTabKey });
      window.kuika ??= {};
      window.kuika[`${this.props.kuikaid}_lastActiveTab`] = activeTabKey;
      return;
    }

    if (this.props.onChange) {
      this.shouldPreventOnChangeFunction = true;
      this.props.onChange(activeTabKey);
    }

    this.handleTabItemOnActive(activeTabKey);
  };

  handleTabItemOnActive = (activeTabKey: string | number | undefined) => {
    if (!this.props.children) return;

    let activeChild;
    if (Array.isArray(this.props.children)) {
      activeChild = this.props.children.find(
        (tabItem: any) =>
          (tabItem.key === activeTabKey || tabItem.props?.kuikaid === activeTabKey) && tabItem.props?.onActive
      );
    } else {
      const child = this.props.children;
      if ((child.key === activeTabKey || child.props?.kuikaid === activeTabKey) && child.props?.onActive) {
        activeChild = child;
      }
    }

    if (!activeChild) return;

    activeChild.props.onActive();
  };

  setDynamicStyle = async () => {
    await this.style.setDynamicCss(this.getDynamicCss());
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }
    let result = "";

    const { selectedTabColor, tabPosition } = this.props;
    const {
      textAlign,
      fontSize,
      fontFamily,
      fontWeight,
      fontStyle,
      color,
      lineHeight,
      letterSpacing,
      textTransform,
      textDecoration
    }: any = this.props.style || {};

    const fontStyles: string[] = [];
    if (fontSize) fontStyles.push(`font-size: ${fontSize} !important;`);
    if (fontFamily) fontStyles.push(`font-family: ${fontFamily} !important;`);
    if (fontWeight) fontStyles.push(`font-weight: ${fontWeight} !important;`);
    if (fontStyle) fontStyles.push(`font-style: ${fontStyle} !important;`);
    if (color) fontStyles.push(`color: ${color};`);
    if (lineHeight) fontStyles.push(`line-height: ${lineHeight} !important;`);
    if (letterSpacing) fontStyles.push(`letter-spacing: ${letterSpacing} !important;`);
    if (textTransform) fontStyles.push(`text-transform: ${textTransform} !important;`);
    if (textDecoration) fontStyles.push(`text-decoration: ${textDecoration} !important;`);

    if (fontStyles.length > 0) {
      result += `.${className.trim()} .rc-tabs-nav .rc-tabs-tab,
                 .${className.trim()} .rc-tabs-nav .rc-tabs-tab:hover,
                 .${className.trim()} .rc-tabs-nav .rc-tabs-tab:focus,
                 .${className.trim()} .rc-tabs-nav .rc-tabs-tab:active,
                 .${className.trim()} .rc-tabs-nav .rc-tabs-tab-active {
          ${fontStyles.join("\n          ")}
        }`;
    }

    if (selectedTabColor) {
      result += `.${className.trim()} .rc-tabs-tab-active, .rc-tabs-tab-active:active, .rc-tabs-tab-active:hover, .rc-tabs-tab-active:focus, .rc-tabs-tab:hover, .rc-tabs-tab:focus, .rc-tabs-tab:active {
          color: ${this.props.selectedTabColor} !important;
        }`;
      result += `.${className.trim()} .rc-tabs-tab-disabled, .rc-tabs-tab-disabled:hover, .rc-tabs-tab-disabled:active, .rc-tabs-tab-disabled:focus {
          color: #ccc !important;
        }`;
      result += `.${className.trim()} .rc-tabs-tab {
          transition: none !important;
        }`;
      result += `.${className.trim()} .rc-tabs-ink-bar {
          background: ${this.props.selectedTabColor};
        }`;
    }
    result += `.${className.trim()} .rc-tabs-tabpane {
        padding: 0px !important;
        overflow: hidden !important;
    }`;
    result += `.${className.trim()} .rc-tabs-tab {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }`;

    result += `.${className.trim()} {
      display: block !important;
      align-items: unset !important;
    }`;

    result += `.${className.trim()} .rc-tabs-bar {
      margin: 0 0 16px !important;
      border-color: transparent !important;
    }`;

    result += `.${className.trim()}.rc-tabs-left .rc-tabs-tab {
      padding-right: 8px !important;
    }`;

    result += `.${className.trim()}.rc-tabs-right .rc-tabs-tab {
      padding-left: 8px !important;
    }`;

    result += `.${className.trim()}.rc-tabs-left .rc-tabs-bar {
      margin-right: 16px !important;
    }`;

    result += `.${className.trim()}.rc-tabs-right .rc-tabs-bar {
      margin-left: 16px !important;
    }`;

    result += `.${className.trim()}.rc-tabs-bottom .rc-tabs-bar {
      margin-top: 16px !important;
    }`;

    result += `.${className.trim()} .rc-tabs-tabpane-active {
      padding: 0px !important;
      flex: 1;
    }`;

    result += `.${className.trim()}.rc-tabs-right .rc-tabs-nav-scroll,
    .${className.trim()}.rc-tabs-left .rc-tabs-nav-scroll {
      height: auto;
    }`;

    result += `.${className.trim()}.rc-tabs-left,
    .${className.trim()}.rc-tabs-right {
      display: flex !important;
    }`;

    result += `.${className.trim()}.rc-tabs-right {
      flex-direction: row-reverse;
    }`;

    result += `.${className.trim()} .rc-tabs-content {
      flex: 1;
      display: flex;
      flex-direction: column;
    }`;

    result += `.${className.trim()} {
      border-bottom: none !important;
    }`;

    if (textAlign && textAlign !== "-webkit-left") {
      result += `.${className.trim()}.rc-tabs-top .rc-tabs-nav-container,
      .${className.trim()}.rc-tabs-bottom .rc-tabs-nav-container {
        text-align: ${textAlign} !important;
      }`;

      result += `.${className.trim()} .rc-tabs-nav-wrap {
        justify-content: ${textAlign === "-webkit-right" ? "flex-end" : "center"} !important;
        display: flex !important;
      }`;

      result += `.${className.trim()} .rc-tabs-nav {
        display: inline-flex !important;
        float: none !important;
        ${textAlign === "center" ? "margin: 0 auto !important;" : ""}
        ${textAlign === "right" ? "margin-left: auto !important;" : ""}
      }`;

      result += `.${className.trim()}.rc-tabs-left .rc-tabs-nav-container,
      .${className.trim()}.rc-tabs-right .rc-tabs-nav-container {
        align-items: ${textAlign === "-webkit-right" ? "flex-end" : "center"};
      }`;
    }

    if (tabPosition === "left" || tabPosition === "right") {
      result += `.${className.trim()} .rc-tabs-content.rc-tabs-content-animated {
        transform: unset !important;
      }`;
      result += `.${className.trim()} .rc-tabs-tabpane-inactive {
        display: none;
      }`;
      // Vertical tab layout — old rc-tabs used nav-swipe wrapper with flex-direction: column.
      // Higher specificity (3 classes) ensures this wins over textAlign's inline-flex rule.
      result += `.${className.trim()}.rc-tabs-left .rc-tabs-nav,
      .${className.trim()}.rc-tabs-right .rc-tabs-nav {
        display: flex !important;
        flex-direction: column !important;
      }`;
    }

    const children = this.getChildren();
    if (children && children.length > 0) {
      children.forEach((child) => {
        if (child.props?.tabTitleWidth) {
          const tabKey = child.key || child.props?.kuikaid || child.props?.id;
          if (tabKey) {
            result += `.${className.trim()} .rc-tabs-tab[aria-controls="tabpane-${tabKey}-${
              this.props.id || this.props.kuikaid
            }"], .${className.trim()} .rc-tabs-tab[aria-controls="tabpane-${tabKey}"] {
              min-width: ${child.props.tabTitleWidth}px !important;
              text-align: center !important;
            }`;
          }
        }
      });
    }

    if (this.props.justifiedTabTitles) {
      result += `.${className.trim()} .rc-tabs-nav-wrap {
        width: 100% !important;
      }`;

      result += `.${className.trim()} .rc-tabs-nav-scroll {
        width: 100% !important;
      }`;

      result += `.${className.trim()} .rc-tabs-nav {
        width: 100% !important;
        display: flex !important;
        flex-direction: row !important;
      }`;

      result += `.${className.trim()} .rc-tabs-tab {
        flex: 1 !important;
        justify-content: center !important;
        text-align: center !important;
      }`;
    }

    return result;
  };

  getClassName = () => {
    if (this.cachedClassName && this.lastPropsClassName === this.props.className) {
      return this.cachedClassName;
    }

    const uniqueKeyPart = this.uniqueKey ? `ktab_${this.uniqueKey.toString().substring(0, 8)}` : "";
    this.cachedClassName = this.props.className ? `${this.props.className} ${uniqueKeyPart}` : uniqueKeyPart;
    this.lastPropsClassName = this.props.className;

    return this.cachedClassName;
  };

  // Generate a key based on children's editability and visibility values for cache invalidation
  private getChildrenStateKey(): string {
    if (!this.props.children) return "";
    if (!Array.isArray(this.props.children)) {
      const props = this.props.children.props;
      return `${props?.editability || ""}:${props?.visibility || ""}`;
    }
    return this.props.children
      .map((child: any) => `${child?.props?.editability || ""}:${child?.props?.visibility || ""}`)
      .join("|");
  }

  getChildren() {
    const stateKey = this.getChildrenStateKey();

    if (this.props.children === this.lastChildrenProp && stateKey === this.lastChildrenStateKey) {
      return this.processedChildren;
    }

    this.lastChildrenProp = this.props.children;
    this.lastChildrenStateKey = stateKey;

    if (!this.props.children) {
      this.processedChildren = [];
      return this.processedChildren;
    }

    if (!Array.isArray(this.props.children)) {
      if (this.props.children.props && this.props.children.props.visibility !== "hidden") {
        this.processedChildren = [this.props.children];
        return this.processedChildren;
      }
      this.processedChildren = [];
      return this.processedChildren;
    }

    if (!this.props.children.length) {
      this.processedChildren = [];
      return this.processedChildren;
    }

    this.processedChildren = this.props.children
      .filter((child: any) => child.props && child.props.visibility !== "hidden")
      .map((child: any) => {
        // Only clone when we actually need to set disabled — avoids creating new element references
        if (child.props.editability === "disabled") {
          return React.cloneElement(child, { disabled: true });
        }
        return child;
      });

    return this.processedChildren;
  }

  getActiveKey = (): string | number | undefined => {
    if (window.kuika?.dashboardState === DashboardState.design) {
      return this.state.designTimeValue;
    }

    let activeKey: string | number | undefined = this.props.value;
    let keyProp = "id";
    if (window.kuika?.dashboardState === 1) {
      activeKey = this.props.activeKey;
      keyProp = "kuikaid";
    }

    if (!activeKey && this.props.children) {
      if (Array.isArray(this.props.children)) {
        if (this.props.children[0] && this.props.children[0].props) {
          if (this.props.children[0].props.visibility === "hidden") {
            const nextTab = this.props.children.find(
              (child: any) => child.props && child.props.visibility !== "hidden"
            );
            if (nextTab) {
              activeKey = nextTab.props[keyProp];
            }
          } else if (this.props.children[0].props[keyProp]) {
            activeKey = this.props.children[0].props[keyProp];
          }
        }
      } else if (this.props.children.props) {
        activeKey = this.props.children.props[keyProp];
      }
    }

    return activeKey;
  };

  private renderTab(child: React.ReactElement, activeKey: string | number | undefined): ReactNode {
    const tabKey = child.key || child.props?.kuikaid || child.props?.id;
    const isActive = tabKey === activeKey || child.props?.kuikaid === activeKey;
    const isDisabled = child.props?.disabled === true;
    const title = child.props?.tab || child.props?.["data-title"] || child.props?.title || "";
    const icon = child.props?.icon;
    const iconColor = child.props?.iconColor;

    const parentId = this.props.id || this.props.kuikaid;
    const paneId = parentId ? `tabpane-${tabKey}-${parentId}` : `tabpane-${tabKey}`;

    // Pre-computed class strings to avoid array allocation + join()
    const className = isActive
      ? isDisabled
        ? "rc-tabs-tab rc-tabs-tab-active rc-tabs-tab-disabled"
        : "rc-tabs-tab rc-tabs-tab-active"
      : isDisabled
        ? "rc-tabs-tab rc-tabs-tab-disabled"
        : "rc-tabs-tab";

    return (
      <div
        key={tabKey}
        role="tab"
        className={className}
        aria-controls={paneId}
        aria-selected={isActive}
        aria-disabled={isDisabled}
        tabIndex={isActive ? 0 : -1}
        onClick={
          isDisabled
            ? undefined
            : () => {
                this.handleOnChangeTab(tabKey, true);
              }
        }
      >
        {icon && icon.length > 0 && (
          <Icon
            style={{
              fontSize: this.props.style?.fontSize || "20px",
              color: iconColor || this.props.style?.color || "inherit",
              paddingBottom: "2px",
              position: title ? "relative" : undefined,
              marginRight: title ? "4px" : undefined
            }}
            iconName={icon}
          />
        )}
        {title}
      </div>
    );
  }

  private renderTabBar(children: React.ReactElement[], activeKey: string | number | undefined): ReactNode {
    const { scrollOffset, showArrows, showPrev, showNext } = this.state;
    const tabPosition = this.props.tabPosition || "top";
    const isHorizontal = tabPosition === "top" || tabPosition === "bottom";

    const navTransform = isHorizontal
      ? `translate3d(${scrollOffset}px, 0px, 0px)`
      : `translate3d(0px, ${scrollOffset}px, 0px)`;

    const prevClasses = ["rc-tabs-tab-prev"];
    if (!showPrev) prevClasses.push("rc-tabs-tab-btn-disabled");
    if (showArrows) prevClasses.push("rc-tabs-tab-arrow-show");

    const nextClasses = ["rc-tabs-tab-next"];
    if (!showNext) nextClasses.push("rc-tabs-tab-btn-disabled");
    if (showArrows) nextClasses.push("rc-tabs-tab-arrow-show");

    const containerClasses = ["rc-tabs-nav-container"];
    if (showArrows) containerClasses.push("rc-tabs-nav-container-scrolling");

    return (
      <div role="tablist" className="rc-tabs-bar">
        <div ref={this.navContainerRef} className={containerClasses.join(" ")}>
          <span className={prevClasses.join(" ")} onClick={showPrev ? this.handleScrollPrev : undefined}>
            <span className="rc-tabs-tab-prev-icon" />
          </span>
          <span className={nextClasses.join(" ")} onClick={showNext ? this.handleScrollNext : undefined}>
            <span className="rc-tabs-tab-next-icon" />
          </span>
          <div ref={this.navWrapRef} className="rc-tabs-nav-wrap">
            <div className="rc-tabs-nav-scroll">
              <div ref={this.navRef} className="rc-tabs-nav" style={{ transform: navTransform }}>
                {children.map((child) => this.renderTab(child, activeKey))}
                <div ref={this.inkBarRef} className="rc-tabs-ink-bar rc-tabs-ink-bar-no-animated" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderTabContent(children: React.ReactElement[], activeKey: string | number | undefined): ReactNode {
    const parentId = this.props.id || this.props.kuikaid;

    const childrenChanged = children !== this.lastContentChildrenRef;
    const parentIdChanged = parentId !== this.lastContentParentId;
    const activeKeyChanged = activeKey !== this.lastContentActiveKey;

    if (!childrenChanged && !parentIdChanged && !activeKeyChanged) {
      // Nothing changed — return exact same JSX object so React skips reconciliation entirely
      return this.cachedTabContentJSX;
    }

    if (childrenChanged || parentIdChanged) {
      // Full rebuild — children array or parentId changed
      this.contentCloneCache.clear();
      for (const child of children) {
        const tabKey = child.key || child.props?.kuikaid || child.props?.id;
        const isActive = tabKey === activeKey || child.props?.kuikaid === activeKey;
        this.contentCloneCache.set(
          String(tabKey),
          React.cloneElement(child, {
            _active: isActive,
            _tabKey: tabKey,
            _parentId: parentId
          })
        );
      }
    } else {
      // Partial update — only re-clone the previously active and newly active tabs.
      // All other tabs keep their cached element reference, so React skips them entirely.
      const prevActiveStr = String(this.lastContentActiveKey);
      for (const child of children) {
        const tabKey = child.key || child.props?.kuikaid || child.props?.id;
        const tabKeyStr = String(tabKey);
        const isActive = tabKey === activeKey || child.props?.kuikaid === activeKey;
        const wasActive = tabKeyStr === prevActiveStr || child.props?.kuikaid === this.lastContentActiveKey;

        if (isActive || wasActive) {
          this.contentCloneCache.set(
            tabKeyStr,
            React.cloneElement(child, {
              _active: isActive,
              _tabKey: tabKey,
              _parentId: parentId
            })
          );
        }
      }
    }

    this.lastContentActiveKey = activeKey;
    this.lastContentParentId = parentId;
    this.lastContentChildrenRef = children;

    this.cachedTabContentJSX = (
      <div className="rc-tabs-content rc-tabs-content-no-animated">
        {children.map((child) => {
          const tabKey = child.key || child.props?.kuikaid || child.props?.id;
          return this.contentCloneCache.get(String(tabKey));
        })}
      </div>
    );

    return this.cachedTabContentJSX;
  }

  render(): ReactNode {
    const activeKey = this.getActiveKey();
    const children = this.getChildren();
    const tabPosition = this.props.tabPosition || "top";
    const className = this.getClassName();

    // Cache root class string — only rebuild when tabPosition or className changes
    if (tabPosition !== this.lastTabPosition || className !== this.lastRootClassName) {
      this.cachedRootClasses = className
        ? `rc-tabs rc-tabs-${tabPosition} ${className}`
        : `rc-tabs rc-tabs-${tabPosition}`;
      this.lastTabPosition = tabPosition;
      this.lastRootClassName = className;
    }

    const tabBar = this.renderTabBar(children, activeKey);
    const tabContent = this.renderTabContent(children, activeKey);

    return (
      <div ref={this.tabsRef} className={this.cachedRootClasses} style={this.props.style} data-testid="rc-tabs">
        {tabPosition === "bottom" ? (
          <>
            {tabContent}
            {tabBar}
          </>
        ) : (
          <>
            {tabBar}
            {tabContent}
          </>
        )}
      </div>
    );
  }
}

const tab = withCommonEvents(Tab);
export { tab as Tab };
